import { memo } from 'react';
import { ArrowUpRight, ChevronDown, Newspaper, Search } from 'lucide-react';
import { useMemo, useState } from 'react';

import { SentimentBadge } from '@/components/SentimentBadge';
import { collectNews } from '@/features/news/newsUtils';
import type { EnrichedOpportunity } from '@/models/marketData';
import { formatIstNewsTime, formatNumber } from '@/utils/formatters';
import { sentimentType } from '@/utils/marketCalculations';

type NewsFilter = 'all' | 'positive' | 'neutral' | 'negative';

export const NewsSection = memo(({ opportunities }: { opportunities: EnrichedOpportunity[] }) => {
  const [search, setSearch] = useState('');
  const [sentiment, setSentiment] = useState<NewsFilter>('all');
  const [expanded, setExpanded] = useState(false);
  const news = useMemo(() => collectNews(opportunities), [opportunities]);
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return news.filter((article) => {
      const matchesQuery =
        !query ||
        [article.heading, article.summary, article.shortSummary, ...article.symbols]
          .join(' ')
          .toLowerCase()
          .includes(query);
      const matchesSentiment =
        sentiment === 'all' || sentimentType(article.sentiment) === sentiment;
      return matchesQuery && matchesSentiment;
    });
  }, [news, search, sentiment]);
  const visible = expanded ? filtered : filtered.slice(0, 6);
  const positiveCount = news.filter(
    ({ sentiment: score }) => sentimentType(score) === 'positive',
  ).length;
  const negativeCount = news.filter(
    ({ sentiment: score }) => sentimentType(score) === 'negative',
  ).length;

  return (
    <section className="panel news-panel" id="market-news">
      <div className="news-panel__header">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Context stream</span>
            <h2>Related market news</h2>
          </div>
          <div className="news-summary">
            <span>{news.length} stories</span>
            <span className="positive">{positiveCount} positive</span>
            <span className="negative">{negativeCount} negative</span>
          </div>
        </div>
        <div className="news-controls">
          <label className="filter-field filter-field--wide">
            <Search size={15} aria-hidden="true" />
            <span className="sr-only">Search related news</span>
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search stories or symbols"
            />
          </label>
          <label className="filter-field">
            <span className="sr-only">Filter news sentiment</span>
            <select
              value={sentiment}
              onChange={(event) => setSentiment(event.target.value as NewsFilter)}
            >
              <option value="all">All sentiment</option>
              <option value="positive">Positive</option>
              <option value="neutral">Neutral</option>
              <option value="negative">Negative</option>
            </select>
          </label>
        </div>
      </div>
      <div className="news-grid">
        {visible.map((article) => {
          const type = sentimentType(article.sentiment);
          return (
            <article className="news-card" key={article.articleLink}>
              <div className="news-card__image">
                {article.thumbnail ? (
                  <img src={article.thumbnail} alt="" loading="lazy" />
                ) : (
                  <div className="news-image-fallback">
                    <Newspaper size={24} />
                  </div>
                )}
                <div className="news-card__symbols">
                  {article.symbols.slice(0, 3).map((symbol) => (
                    <span key={symbol}>{symbol}</span>
                  ))}
                </div>
              </div>
              <div className="news-card__body">
                <div className="news-card__meta">
                  <SentimentBadge
                    type={type}
                    label={`${type} ${article.sentiment > 0 ? '+' : ''}${formatNumber(article.sentiment, 2)}`}
                  />
                  <span>{formatIstNewsTime(article.publishedAt)}</span>
                </div>
                <h3>{article.heading}</h3>
                <p>{article.shortSummary || article.summary}</p>
                <a href={article.articleLink} target="_blank" rel="noopener noreferrer">
                  Read story <ArrowUpRight size={14} />
                </a>
              </div>
            </article>
          );
        })}
        {visible.length === 0 && (
          <div className="news-empty">
            <Newspaper size={23} />
            <strong>No matching stories</strong>
            <span>Try a different symbol, keyword or sentiment.</span>
          </div>
        )}
      </div>
      {filtered.length > 6 && (
        <button type="button" className="show-news-button" onClick={() => setExpanded(!expanded)}>
          {expanded ? 'Show latest six' : `Show all ${filtered.length} stories`}
          <ChevronDown className={expanded ? 'is-rotated' : ''} size={15} />
        </button>
      )}
    </section>
  );
});
